import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { GOTO_SHORTCUTS } from './navigation';
import { ui } from './ui-store';

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

/**
 * Global keyboard shortcuts:
 * ⌘K / Ctrl K palette · / palette · ? shortcuts · [ sidebar · g then a key to jump.
 */
export function useGlobalHotkeys(): void {
  const navigate = useNavigate();
  const pendingG = useRef<number | null>(null);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        ui.setPaletteOpen(true);
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return;
      if (document.querySelector('[role="dialog"]')) return;

      if (pendingG.current !== null) {
        window.clearTimeout(pendingG.current);
        pendingG.current = null;
        const target = GOTO_SHORTCUTS.find((s) => s.key === event.key.toLowerCase());
        if (target) {
          event.preventDefault();
          void navigate(target.to);
        }
        return;
      }

      switch (event.key) {
        case '/':
          event.preventDefault();
          ui.setPaletteOpen(true);
          break;
        case '?':
          event.preventDefault();
          ui.setShortcutsOpen(true);
          break;
        case '[':
          event.preventDefault();
          ui.toggleSidebar();
          break;
        case 'g':
          pendingG.current = window.setTimeout(() => {
            pendingG.current = null;
          }, 1200);
          break;
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate]);
}
